import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { ButtonAppearance, ButtonSize } from '@vsolv/vectors-ui/button';
import { IconName } from '@vsolv/vectors-ui/icons';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { DeclarationDownloadResult, DeclarationDownloadService } from '../../services/declaration-download.service';

@Component({
  selector: 'ws-view-declaration-button',
  templateUrl: './view-declaration-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ViewDeclarationButtonComponent {
  @Input() warrantyId?: string;
  @Input() contractNumber?: string;
  @Input() appearance: ButtonAppearance = 'outline';
  @Input() theme: ThemeColor = 'default';
  @Input() size: ButtonSize = 'md';
  @Input() icon: IconName | null = 'file-check-02';
  @Input() loadingIcon: IconName = 'loading-01';
  @Input() label = 'View Registration';
  @Input() generatingLabel = 'Generating...';
  @Input() buttonClass?: string;
  @Input() stopPropagation = false;
  @Input() disabled = false;
  @Input() cleanupDelayMs?: number;

  @Input() showSuccessToast = true;
  @Input() successTitle = 'Registration Ready';
  @Input() openedText = 'Your warranty registration opened in a new tab.';
  @Input() downloadedText = 'Your warranty registration has been downloaded.';

  @Input() showBlockedToast = true;
  @Input() blockedTitle = 'Pop-up Blocked';
  @Input() blockedText = 'Please allow pop-ups to view the registration.';

  @Input() errorTitle = 'Failed to load registration';
  @Input() errorText = 'Unable to generate registration. Please try again or contact support.';

  @Input() downloadFileName?: string;

  @Output() declarationOpened = new EventEmitter<DeclarationDownloadResult>();
  @Output() declarationFailed = new EventEmitter<unknown>();

  generating = false;

  constructor(
    private readonly declarationSvc: DeclarationDownloadService,
    private readonly toastSvc: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  async onClick(event: MouseEvent): Promise<void> {
    if (this.stopPropagation) {
      event.stopPropagation();
    }

    if (this.disabled || this.generating || !this.warrantyId) {
      return;
    }

    this.generating = true;
    this.cdr.markForCheck();

    try {
      const result = await this.declarationSvc.openDeclaration({
        warrantyId: this.warrantyId,
        contractNumber: this.contractNumber,
        fileName: this.downloadFileName,
        cleanupDelayMs: this.cleanupDelayMs,
      });

      this.declarationOpened.emit(result);

      if (result.status === 'downloaded') {
        if (this.showBlockedToast) {
          this.toastSvc.show({
            type: 'warning',
            title: this.blockedTitle,
            text: this.blockedText,
          });
        } else if (this.showSuccessToast) {
          this.toastSvc.show({
            type: 'success',
            title: this.successTitle,
            text: this.downloadedText,
          });
        }
      } else if (this.showSuccessToast) {
        this.toastSvc.show({
          type: 'success',
          title: this.successTitle,
          text: this.openedText,
        });
      }
    } catch (error: unknown) {
      this.toastSvc.show({
        type: 'error',
        title: this.errorTitle,
        text: error instanceof Error ? error.message : this.errorText,
      });
      this.declarationFailed.emit(error);
    } finally {
      this.generating = false;
      this.cdr.markForCheck();
    }
  }
}
